import { Menu, User, Settings } from 'lucide-react';
import { UserButton } from '@clerk/clerk-react';
import { Button } from '@/components/ui/button';
import { useAppStore } from '@/stores/appStore';
import { ConnectionStatus } from '@/components/ConnectionStatus';
import { ActionModal } from '@/components/ActionModal';
import { OrganizationSwitcher } from '@/components/OrganizationSwitcher';
import { useBreakpoint } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import config from '@/config';

export function Header() {
  const { toggleSidebar, user } = useAppStore();
  const { isMobile, isSmallMobile } = useBreakpoint();

  return (
    <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className={cn(
        'flex items-center',
        isMobile ? 'h-14 px-2' : 'h-16 px-4'
      )}>
        <div className={cn(
          'flex items-center',
          isMobile ? 'gap-2' : 'gap-4'
        )}>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className={cn(
              isMobile ? 'h-8 w-8' : 'h-9 w-9'
            )}
          >
            <Menu className={cn(
              isMobile ? 'h-3.5 w-3.5' : 'h-4 w-4'
            )} />
            <span className="sr-only">Toggle sidebar</span>
          </Button>

          <div className={cn(
            'flex items-center',
            isMobile ? 'gap-1.5' : 'gap-2'
          )}>
            {/* Company Logo */}
            <div className="group">
              <div className="flex gap-[2px] h-[22px]">
                <div className="w-[8px] h-full rounded-bl-md transition-colors duration-200 bg-primary group-hover:bg-primary/90" style={{ backgroundColor: '#2f45ff' }}></div>
                <div className="w-[8px] h-full transition-colors duration-200 bg-primary group-hover:bg-primary/90" style={{ backgroundColor: '#2f45ff' }}></div>
                <div className="w-[8px] h-full rounded-br-md transition-colors duration-200 bg-primary group-hover:bg-primary/90" style={{ backgroundColor: '#2f45ff' }}></div>
              </div>
            </div>
            <h1 className={cn(
              'font-semibold',
              isSmallMobile ? 'text-sm' : isMobile ? 'text-base' : 'text-lg'
            )}>
              {isSmallMobile ? 'Wrnt AI' : 'Wrnt AI Agent'}
            </h1>
          </div>
        </div>

        <div className={cn(
          'ml-auto flex items-center',
          isMobile ? 'gap-2' : 'gap-4'
        )}>
          <ConnectionStatus />

          {/* Organization Switcher */}
          {config.CLERK_PUBLISHABLE_KEY && !isMobile && (
            <OrganizationSwitcher />
          )}

          {/* Action Management - Mobile only */}
          {isMobile && (
            <ActionModal 
              trigger={
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    isMobile ? 'h-8 w-8' : 'h-9 w-9'
                  )}
                  aria-label="Manage Actions"
                >
                  <Settings className={cn(
                    isMobile ? 'h-3.5 w-3.5' : 'h-4 w-4'
                  )} />
                </Button>
              }
            />
          )}

          {/* User Profile Section */}
          {config.CLERK_PUBLISHABLE_KEY ? (
            <div className="flex items-center gap-2">
              {!isSmallMobile && (
                <span className={cn(
                  'text-muted-foreground',
                  isMobile ? 'text-xs' : 'text-sm'
                )}>
                  {user.name}
                </span>
              )}
              <UserButton
                appearance={{
                  elements: {
                    avatarBox: cn(
                      'rounded-full',
                      isMobile ? 'h-7 w-7' : 'h-8 w-8'
                    )
                  }
                }}
                showName={false}
                userProfileMode="navigation"
                userProfileUrl="/user-profile"
              />
            </div>
          ) : (
            !isSmallMobile && (
              <div className={cn(
                'flex items-center gap-2 text-muted-foreground',
                isMobile ? 'text-xs' : 'text-sm'
              )}>
                <User className={cn(
                  isMobile ? 'h-3 w-3' : 'h-4 w-4'
                )} />
                <span>{user.name}</span>
              </div>
            )
          )}
        </div>
      </div>
    </header>
  );
}